import { AutocompleteRenderOptionState, Chip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import moment from 'moment';
import { useAppSelector, useShallowSelector } from '../../app/hooks';
import {
  getModSymbolMapping,
  selectUnavailableItemsByName,
} from '../../selectors/menu';
import { getHightlight, getQuantityFromInput } from '../../utils/autocomplete';
import Colors from '../../utils/color';
import {
  TopLevelMenuItem,
  isItem86edToday,
  splitPathToItem,
} from '../../utils/menu';
import { ParsedMenuItem } from '../../types/menu';
import { selectActiveRestaurantTimezone } from '../../selectors/restaurant';
import { isBMModifierAnUnavailableConstraint } from '../../utils/menuUnification.utils';
import { getMenuUnificationFeatureFlag } from '../../utils/session-storage';
import useSelectedCartItems from '../../hooks/useSelectedItems/useSelectedCartItems.hooks';
import { selectDialog } from '../../selectors/dialog';
import { processModifierSelection } from './util/helper';

const useStyles = makeStyles(() => ({
  modifier: {
    backgroundColor: '#ab51ff',
    color: 'white',
    borderRadius: '10px',
    fontSize: '.75rem',
    padding: '0 7px',
    marginLeft: '5px',
  },
  selectionBox: {
    width: '15px',
    display: 'inline-block',
  },
  highlightText: {
    marginRight: '15px',
    color: (props: any) =>
      props.isUnavailable ? Colors.red : props.isModifier && '#ab51ff',
    textDecoration: (props: any) => props.isUnavailable && 'line-through',
  },
  unavailable: {
    backgroundColor: `${Colors.red} !important`,
    height: '18px !important',
    marginLeft: '10px',
    '& .MuiChip-label': {
      padding: '0 7px',
      fontSize: '.75rem',
      color: 'white',
    },
  },
}));

interface IAutocompleteOption {
  htmlElemProps: React.HTMLAttributes<HTMLLIElement>;
  menuItem: ParsedMenuItem | TopLevelMenuItem;
  state: AutocompleteRenderOptionState;
  handleOutOfOrderSearch: boolean;
  canUpsellInAIEscalation: Function;
  inputModSymbol: string;
  // hitl_3_0 only: allow selecting an option by mouse click (see the <li>
  // pointerEvents below). Legacy keeps the keyboard-only behavior.
  isHitl3?: boolean;
}
export const AutocompleteOption = ({
  htmlElemProps,
  menuItem,
  state,
  handleOutOfOrderSearch,
  canUpsellInAIEscalation,
  inputModSymbol,
  isHitl3 = false,
}: IAutocompleteOption) => {
  const unavailableItemsByName = useAppSelector(selectUnavailableItemsByName);
  const timezone = useAppSelector(selectActiveRestaurantTimezone);
  const tzAbbreviation = moment.tz(timezone).format('z');
  const isModifier = menuItem.category === 'modifier';
  const is86edToday = isItem86edToday(menuItem);
  const { inactive, activeTimePeriod, slashCommandIdx, slashCommands } =
    menuItem as TopLevelMenuItem;
  const { cart, selectedItem, selectedCartItem, selectedItemCartId } =
    useSelectedCartItems();
  const dialogState = useAppSelector(selectDialog);
  const { selectedModGroup, navigationLevel, parentModGroupId } = dialogState;

  let modifierTags: string[] = [];
  let parentModifier = '';
  if (isModifier && selectedItem) {
    const modifier = menuItem as ParsedMenuItem;
    if (modifier.pathToItem) {
      modifierTags = splitPathToItem(modifier.pathToItem);
    } else {
      modifierTags.push(
        modifier.parentModifierGroupDisplayName ||
          modifier.parentModifierGroupName ||
          selectedItem.name
      );
    }
    parentModifier =
      modifierTags.length > 1
        ? modifierTags[modifierTags.length - 2]
        : selectedItem.name;
  }

  const isUnifiedMenu = getMenuUnificationFeatureFlag();

  const isBMUnavailable =
    menuItem.pathToItem &&
    selectedCartItem &&
    isBMModifierAnUnavailableConstraint(
      selectedCartItem,
      unavailableItemsByName,
      parentModifier,
      menuItem.pathToItem,
      menuItem.parentModifierGroupDisplayName ||
        menuItem.parentModifierGroupName ||
        selectedItem?.name ||
        '',
      menuItem.name
    );

  const isOptionUnavailable =
    is86edToday || inactive || canUpsellInAIEscalation(menuItem);
  const isUnavailable = isUnifiedMenu
    ? !menuItem.available || isBMUnavailable || isOptionUnavailable
    : isOptionUnavailable;

  const styleProps = { isModifier, isUnavailable };
  const classes = useStyles(styleProps);

  const modSymbolMapping = useShallowSelector(getModSymbolMapping);

  const { quantity, inputValue } = getQuantityFromInput(
    state.inputValue,
    modSymbolMapping
  );
  const highlight = getHightlight(
    handleOutOfOrderSearch && inputValue.split(' ').length > 1
      ? inputValue.split(' ')[1]
      : inputValue,
    menuItem,
    quantity
  );

  let ttsPhrase = '';
  if (
    slashCommands &&
    slashCommandIdx !== undefined &&
    slashCommandIdx < slashCommands.length
  ) {
    ttsPhrase = ':' + slashCommands[slashCommandIdx];
  }

  // Use useMemo to optimize isItemSelected calculation
  const isItemSelected = React.useMemo(() => {
    // If there is no selected item or cart id, nothing is selected
    if (!selectedItem || !selectedItemCartId) return false;

    // Get the cart item from the cart using the selected cart id
    const cartItem = cart.cartItems[selectedItemCartId];

    // If cartItem or menuItem is missing, not selected
    if (!cartItem || !menuItem) return false;

    // Use processModifierSelection to determine selection state
    const { isChecked } = processModifierSelection({
      cartItem,
      menuItem: menuItem as ParsedMenuItem,
      modSymbolMapping,
      selectedModGroup,
      inputModSymbol,
      navigationLevel,
      parentModGroupId,
    });

    // If isChecked is true, the item is not selected
    return Boolean(!isChecked);
  }, [
    selectedItem,
    selectedItemCartId,
    cart.cartItems,
    menuItem,
    selectedModGroup,
    inputModSymbol,
  ]);

  return (
    <li
      data-testid="omnibar-autocomplete-option"
      {...htmlElemProps}
      key={(menuItem as any).pathToItem ?? menuItem.id}
      style={{
        // hitl_3_0: 'auto' makes the option clickable — MUI's click handler in
        // htmlElemProps then selects it. Legacy stays 'none' (keyboard-only).
        pointerEvents: isHitl3 ? 'auto' : 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <div>
        <span className={classes.highlightText}>
          {isItemSelected ? (
            <span className={classes.selectionBox}>&#10004;</span>
          ) : (
            <span className={classes.selectionBox}>&nbsp;&nbsp;&nbsp;</span>
          )}
        </span>
        <span
          dangerouslySetInnerHTML={{ __html: highlight + ttsPhrase }}
          className={classes.highlightText}
        />
        {isModifier ? (
          <>
            {modifierTags.reverse().map((tag) => (
              <span className={classes.modifier}>&lt; {tag}</span>
            ))}
          </>
        ) : (
          <Typography
            color={isUnavailable ? Colors.red : undefined}
            fontSize=".75rem"
            component="span"
          >
            {menuItem.category}
          </Typography>
        )}
        {(is86edToday || (inactive && activeTimePeriod)) && (
          <Chip
            label={
              is86edToday
                ? "86'd"
                : `Active ${activeTimePeriod?.start} - ${activeTimePeriod?.end} ${tzAbbreviation}`
            }
            size="small"
            className={classes.unavailable}
          />
        )}
      </div>
    </li>
  );
};
